import type { DeepPartial, UseFormSetValue } from 'react-hook-form';

import {
  ControlledDeviceSetting,
  DesiredSettingValue,
  DesiredSettingValueKind,
} from '@crossnokaye/typescript-sdk/models/components';

import type {
  DropdownOption,
  TableColumnConfig,
  TableRowConfig,
} from '@repo/sdk/models/components';
import { Namespace } from '@repo/sdk/models/components';

import type { SettingChange, SettingChangeGroup } from '@/components/settings/settings-save.types';
import { getActiveState } from '@/features/equipment-browser/conditional-evaluator/get-active-state';
import { resolveSourceUnits } from '@/features/equipment-browser/utils';
import { getOnOffLabel } from '@/features/equipment-browser/utils/boolean-display';
import { castSettingsValue, getDesiredSettingValueKind } from '@/helpers/settings';
import { formatUnits } from '@/helpers/unit-formatter/unit-formatter';

import {
  DISABLED_TOOLTIP_MAP,
  ESTOPPED,
  FAULTED,
  MODE_COLUMN_ID,
} from './equipment-bulk-edit.constants';
import type {
  BulkEditChangeMetadata,
  BulkEditSettingType,
  ColumnUnitInfo,
  DirtyCellInfo,
  DirtyFieldsMap,
  DisabledColumnsResult,
  DriftedSetting,
  EquipmentBulkEditFormValues,
  EquipmentBulkEditTableData,
} from './equipment-bulk-edit.types';

/**
 * Resets a single form cell to its original (server) value.
 * Shared by the preview pushout and save dialog discard buttons.
 */
export const discardFormCell = (
  setValue: UseFormSetValue<{ values: EquipmentBulkEditFormValues }>,
  defaultValues: Readonly<DeepPartial<{ values: EquipmentBulkEditFormValues }>> | undefined,
  rowId: string,
  columnId: string
): void => {
  const originalValue =
    (defaultValues?.values as EquipmentBulkEditFormValues)?.[rowId]?.[columnId] ?? '';
  setValue(`values.${rowId}.${columnId}`, originalValue, {
    shouldDirty: true,
    shouldTouch: false,
  });
};

export const getColumnInputType = (
  columnId: string,
  rowsToEdit: TableRowConfig[],
  selectedRowIds: string[]
): BulkEditSettingType => {
  const selectedRows = rowsToEdit.filter((r) => selectedRowIds.includes(r.id));

  for (const row of selectedRows) {
    const ref = row.refs?.find((r) => r.columnId === columnId);
    if (ref?.displayType) {
      switch (ref.displayType) {
        case 'dropdown':
        case 'switch':
          return 'dropdown';
        case 'input':
          return 'text';
        default:
          return 'readonly';
      }
    }
  }
  return 'readonly';
};

export const buildBulkEditInitialValues = (rows: TableRowConfig[]): EquipmentBulkEditFormValues => {
  return rows.reduce<EquipmentBulkEditFormValues>((acc, row) => {
    const rowValues = row.refs.reduce<Record<string, string>>((rowAcc, ref) => {
      if (!ref.columnId) return rowAcc;
      if (ref.ref?.namespace !== Namespace.Setting) return rowAcc;
      return { ...rowAcc, [ref.columnId]: ref.value ?? '' };
    }, {});

    return { ...acc, [row.id]: rowValues };
  }, {});
};

export const applyColumnValueToSelection = ({
  values,
  columnId,
  value,
  selectedRowIds,
}: {
  values: EquipmentBulkEditFormValues;
  columnId: string;
  value: string;
  selectedRowIds: string[];
}): EquipmentBulkEditFormValues => {
  if (selectedRowIds.length === 0) {
    return values;
  }

  return selectedRowIds.reduce<EquipmentBulkEditFormValues>((acc, rowId) => {
    if (!acc[rowId] || !(columnId in acc[rowId])) {
      return acc;
    }

    return {
      ...acc,
      [rowId]: {
        ...acc[rowId],
        [columnId]: value,
      },
    };
  }, values);
};

const getBooleanOptions = (): DropdownOption[] => [
  { label: 'On', value: 'true' },
  { label: 'Off', value: 'false' },
];

/** Returns dropdown options that exist in ALL selected rows. */
const getIntersectedOptions = (
  columnId: string,
  rowsToEdit: TableRowConfig[],
  selectedRowIds: string[]
): DropdownOption[] => {
  const selectedRows = rowsToEdit.filter((r) => selectedRowIds.includes(r.id));

  const optionSets = selectedRows
    .map((row) => {
      const ref = row.refs?.find((r) => r.columnId === columnId);
      return ref?.displayOptions ?? [];
    })
    .filter((opts) => opts.length > 0);

  if (optionSets.length === 0) return [];

  const [first, ...rest] = optionSets;
  return first.filter((opt) => rest.every((set) => set.some((o) => o.value === opt.value)));
};

export const getOptionsForColumn = (
  columnId: string,
  rowsToEdit: TableRowConfig[],
  selectedRowIds: string[]
): DropdownOption[] => {
  const selectedRows = rowsToEdit.filter((r) => selectedRowIds.includes(r.id));
  const firstRef = selectedRows
    .flatMap((r) => r.refs ?? [])
    .find((ref) => ref.columnId === columnId);

  if (firstRef?.displayType === 'switch') {
    return getBooleanOptions();
  }

  return getIntersectedOptions(columnId, rowsToEdit, selectedRowIds);
};

/**
 * Returns the common display value for a column across selected rows, or
 * undefined when values differ (or no rows have the column).
 * Used by the bulk-edit sidebar to suppress the dirty indicator when the
 * entered value already matches every selected cell.
 */
export const getCommonDisplayValue = (
  columnId: string,
  inputType: BulkEditSettingType,
  rows: TableRowConfig[],
  selectedRowIds: string[]
): string | undefined => {
  const selectedRows = rows.filter((r) => selectedRowIds.includes(r.id));
  const displayValues = new Set<string>();

  for (const row of selectedRows) {
    const ref = row.refs?.find((r) => r.columnId === columnId);
    if (!ref) continue;

    const sourceValue = ref.value ?? '';

    if (inputType === 'dropdown') {
      displayValues.add(sourceValue);
    } else {
      const displayUnits = ref.displayUnits;
      const sourceUnits = resolveSourceUnits(displayUnits, ref.ref?.units);
      const formatted = formatUnits({ value: sourceValue, units: sourceUnits }, displayUnits);
      displayValues.add(formatted.valueLabel ?? sourceValue);
    }
  }

  return displayValues.size === 1 ? [...displayValues][0] : undefined;
};

export const getColumnUnitInfo = (
  columnId: string,
  rows: TableRowConfig[],
  selectedRowIds: string[]
): ColumnUnitInfo | undefined => {
  const selectedRows = rows.filter((r) => selectedRowIds.includes(r.id));
  const firstRef = selectedRows
    .flatMap((r) => r.refs ?? [])
    .find((ref) => ref.columnId === columnId);

  if (!firstRef) return undefined;

  const displayUnits = firstRef.displayUnits;
  const sourceUnits = resolveSourceUnits(displayUnits, firstRef.ref?.units);
  const unitsLabel = formatUnits(
    { value: 1, units: sourceUnits },
    displayUnits
  ).unitsLabel?.toUpperCase();

  return { displayUnits, sourceUnits, unitsLabel };
};

export const getColumnsWithMismatchedUnits = (
  columns: TableColumnConfig[],
  rows: TableRowConfig[],
  selectedRowIds: string[]
): Set<string> => {
  const mismatched = new Set<string>();
  const selectedRows = rows.filter((r) => selectedRowIds.includes(r.id));

  for (const column of columns) {
    const units = new Set<string>();
    for (const row of selectedRows) {
      const ref = row.refs?.find((r) => r.columnId === column.columnId);
      if (ref) {
        const sourceUnits = resolveSourceUnits(ref.displayUnits, ref.ref?.units);
        if (sourceUnits) units.add(sourceUnits);
      }
    }
    if (units.size > 1) mismatched.add(column.columnId);
  }

  return mismatched;
};

export const getColumnsWithNoOverlappingOptions = (
  columns: TableColumnConfig[],
  rows: TableRowConfig[],
  selectedRowIds: string[]
): Set<string> => {
  const noOverlap = new Set<string>();

  for (const column of columns) {
    if (!column.canEdit) continue;

    const inputType = getColumnInputType(column.columnId, rows, selectedRowIds);
    if (inputType !== 'dropdown') continue;

    const options = getOptionsForColumn(column.columnId, rows, selectedRowIds);
    if (options.length === 0) {
      noOverlap.add(column.columnId);
    }
  }

  return noOverlap;
};

export const getColumnsWithMissingRefs = (
  columns: TableColumnConfig[],
  rows: TableRowConfig[],
  selectedRowIds: string[]
): Set<string> => {
  const missing = new Set<string>();
  const selectedRows = rows.filter((r) => selectedRowIds.includes(r.id));

  for (const column of columns) {
    for (const row of selectedRows) {
      const ref = row.refs?.find((r) => r.columnId === column.columnId);
      if (!ref) {
        missing.add(column.columnId);
        break;
      }
    }
  }

  return missing;
};

/**
 * Identifies all columns that should be disabled in the bulk-edit drawer by
 * combining mismatched units, no-overlap options, and missing refs checks.
 * Priority: unit mismatch > no overlap > missing refs.
 */
export const getDisabledColumns = (
  columns: TableColumnConfig[],
  rows: TableRowConfig[],
  selectedRowIds: string[]
): DisabledColumnsResult => {
  const ids = new Set<string>();
  const reasons = new Map<string, string>();

  for (const columnId of getColumnsWithMismatchedUnits(columns, rows, selectedRowIds)) {
    ids.add(columnId);
    reasons.set(columnId, 'The source units across selected rows do not match!');
  }
  for (const columnId of getColumnsWithNoOverlappingOptions(columns, rows, selectedRowIds)) {
    ids.add(columnId);
    if (!reasons.has(columnId)) {
      reasons.set(columnId, 'No overlapping options across selected devices');
    }
  }
  for (const columnId of getColumnsWithMissingRefs(columns, rows, selectedRowIds)) {
    ids.add(columnId);
    if (!reasons.has(columnId)) {
      reasons.set(columnId, 'Not all selected devices support this setting');
    }
  }

  return { ids, reasons };
};

export const createSnapshot = (
  allValues: EquipmentBulkEditFormValues,
  selectedRowIds: string[]
): EquipmentBulkEditFormValues => {
  return selectedRowIds.reduce<EquipmentBulkEditFormValues>((acc, rowId) => {
    if (allValues[rowId]) {
      acc[rowId] = { ...allValues[rowId] };
    }
    return acc;
  }, {});
};

/**
 * Iterates over each dirty cell in the form, resolving the matching row and ref.
 * Shared by buildDesiredSettingValues, extractDirtySettingIds, and detectSettingDrift.
 */
export const forEachDirtyCell = (
  dirtyFields: DirtyFieldsMap,
  rows: TableRowConfig[],
  callback: (info: DirtyCellInfo) => void
): void => {
  if (!dirtyFields) return;

  const rowMap = new Map(rows.map((r) => [r.id, r]));

  for (const rowId of Object.keys(dirtyFields)) {
    const rowDirty = dirtyFields[rowId];
    if (!rowDirty) continue;

    const row = rowMap.get(rowId);
    if (!row) continue;

    for (const columnId of Object.keys(rowDirty)) {
      if (!rowDirty[columnId]) continue;

      const ref = row.refs?.find((r) => r.columnId === columnId);
      if (!ref?.ref?.id) continue;

      callback({ rowId, columnId, row, ref });
    }
  }
};

/**
 * Transforms dirty bulk-edit form cells into API-ready DesiredSettingValue objects.
 * Form values are stored in source/base units (the input cell converts on change),
 * so no unit conversion is needed here -- only type casting.
 */
export const buildDesiredSettingValues = (
  dirtyFields: DirtyFieldsMap,
  currentValues: EquipmentBulkEditFormValues,
  defaultValues: EquipmentBulkEditFormValues,
  rows: TableRowConfig[]
): DesiredSettingValue[] => {
  const result: DesiredSettingValue[] = [];

  forEachDirtyCell(dirtyFields, rows, ({ rowId, columnId, ref }) => {
    const currentValue = currentValues?.[rowId]?.[columnId] ?? '';
    const originalValue = defaultValues?.[rowId]?.[columnId] ?? '';
    if (currentValue === originalValue) return;

    const valueKind = ref.ref!.valueKind;
    const kind: DesiredSettingValueKind = getDesiredSettingValueKind(valueKind);
    const castedValue = castSettingsValue(currentValue, valueKind);

    result.push({
      settingId: ref.ref!.id,
      kind,
      desiredValue: { [valueKind as string]: castedValue },
    } as DesiredSettingValue);
  });

  return result;
};

/**
 * Collects the unique setting IDs for all dirty form cells.
 * Used to scope the ListSettings drift-check to only the settings the user edited.
 */
export const extractDirtySettingIds = (
  dirtyFields: DirtyFieldsMap,
  rows: TableRowConfig[]
): string[] => {
  const ids = new Set<string>();
  forEachDirtyCell(dirtyFields, rows, ({ ref }) => {
    ids.add(ref.ref!.id);
  });
  return Array.from(ids);
};

export const extractSettingValueAsString = (setting: ControlledDeviceSetting): string => {
  const v = setting.value;
  if (!v) return '';

  if (v.boolean !== undefined) return String(v.boolean);
  if (v.number !== undefined) return String(v.number);
  if (v.enum !== undefined) return v.enum;

  // schedule / sequence values are not bulk-editable; return empty
  return '';
};

/**
 * Compares the form's default values for every dirty cell against the freshly
 * fetched server values from ListSettings. Returns an array of DriftedSetting
 * entries for any cell whose server-side value no longer matches the form default.
 */
export const detectSettingDrift = (
  dirtyFields: DirtyFieldsMap,
  defaultValues: EquipmentBulkEditFormValues,
  rows: TableRowConfig[],
  freshSettings: ControlledDeviceSetting[]
): DriftedSetting[] => {
  const settingMap = new Map(freshSettings.map((s) => [s.settingId, s]));
  const drifted: DriftedSetting[] = [];

  forEachDirtyCell(dirtyFields, rows, ({ rowId, columnId, ref }) => {
    const freshSetting = settingMap.get(ref.ref!.id);
    if (!freshSetting) return;

    const formDefault = defaultValues[rowId]?.[columnId] ?? '';
    const serverValue = extractSettingValueAsString(freshSetting);

    if (formDefault !== serverValue) {
      drifted.push({ settingId: ref.ref!.id, rowId, columnId, formDefault, serverValue });
    }
  });

  return drifted;
};

const getActiveCriticalBadgeLabel = (row: EquipmentBulkEditTableData): string | undefined => {
  const actionComponent = row[MODE_COLUMN_ID]?.actionComponent;

  if (!actionComponent?.states?.length) {
    return undefined;
  }

  const activeState = getActiveState({ states: actionComponent.states });
  const stateName = activeState?.stateName?.toLowerCase();

  if (stateName?.startsWith(FAULTED)) {
    return FAULTED;
  }

  if (stateName?.startsWith(ESTOPPED)) {
    return ESTOPPED;
  }

  return undefined;
};

export const isDeviceFaultedOrEStopped = (row: EquipmentBulkEditTableData): boolean => {
  return getActiveCriticalBadgeLabel(row) !== undefined;
};

export const getDeviceDisabledTooltip = (row: EquipmentBulkEditTableData): string | undefined => {
  const label = getActiveCriticalBadgeLabel(row);
  return label ? DISABLED_TOOLTIP_MAP[label] : undefined;
};

interface BulkEditSettingCellDisabledOptions {
  row: EquipmentBulkEditTableData;
  isPushOutOpen: boolean;
  disabledByPermissions?: boolean;
}

export const isBulkEditSettingCellDisabled = ({
  row,
  isPushOutOpen,
  disabledByPermissions,
}: BulkEditSettingCellDisabledOptions): boolean => {
  return isPushOutOpen || Boolean(disabledByPermissions) || isDeviceFaultedOrEStopped(row);
};

export const getBulkEditSettingCellDisabledTooltip = (
  row: EquipmentBulkEditTableData,
  permissionTooltipText?: string
): string | undefined => {
  return getDeviceDisabledTooltip(row) ?? permissionTooltipText;
};

export const formatCellValue = (
  value: string,
  columnId: string,
  row: TableRowConfig,
  options?: { booleanLabelCase?: 'upper' | 'title' }
): string => {
  const ref = row.refs?.find((r) => r.columnId === columnId);
  if (!ref) return value;

  if (ref.displayOptions?.length) {
    const option = ref.displayOptions.find((o) => o.value === value);
    if (option) return option.label;
  }

  if (ref.enumOptions?.length) {
    const enumOption = ref.enumOptions.find((o) => o.value === value);
    if (enumOption) return enumOption.label;
  }

  if (ref.displayType === 'switch' || ref.displayType === 'badge') {
    // TODO(UX-3724): This currently forces On/Off labels; switch to required boolean displayUnits
    // from display_template when that migration lands. https://crossnokaye.atlassian.net/browse/UX-3724
    const booleanLabel = getOnOffLabel(value, options?.booleanLabelCase ?? 'title');
    if (booleanLabel) return booleanLabel;
  }

  const sourceUnits = resolveSourceUnits(ref.displayUnits, ref.ref?.units);
  const formatted = formatUnits({ value, units: sourceUnits }, ref.displayUnits);
  return formatted.valueUnitsLabel ?? formatted.valueLabel ?? value;
};

const getMeta = (change: SettingChange): BulkEditChangeMetadata =>
  change.metadata as unknown as BulkEditChangeMetadata;

export const groupChangesByEquipment = (changes: SettingChange[]): SettingChangeGroup[] => {
  const map = new Map<string, SettingChangeGroup>();

  for (const change of changes) {
    const meta = getMeta(change);
    const existing = map.get(meta.rowId);
    if (existing) {
      existing.changes.push(change);
    } else {
      map.set(meta.rowId, {
        id: meta.rowId,
        label: meta.rowName,
        changes: [change],
      });
    }
  }

  const groups = Array.from(map.values());
  for (const group of groups) {
    group.changes.sort((a, b) => {
      const aMeta = getMeta(a);
      const bMeta = getMeta(b);
      return aMeta.columnLabel.localeCompare(bMeta.columnLabel);
    });
  }
  return groups.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
};

export const groupChangesBySetting = (changes: SettingChange[]): SettingChangeGroup[] => {
  const map = new Map<string, SettingChangeGroup>();

  for (const change of changes) {
    const meta = getMeta(change);
    const existing = map.get(meta.columnId);
    if (existing) {
      existing.changes.push(change);
    } else {
      map.set(meta.columnId, {
        id: meta.columnId,
        label: meta.columnLabel,
        changes: [change],
      });
    }
  }

  const groups = Array.from(map.values());
  for (const group of groups) {
    group.changes.sort((a, b) => {
      const aMeta = getMeta(a);
      const bMeta = getMeta(b);
      return aMeta.rowName.localeCompare(bMeta.rowName, undefined, { numeric: true });
    });
  }
  return groups.sort((a, b) => a.label.localeCompare(b.label));
};
